import { Check, MagnifyingGlass } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { type KeyboardEvent, type ReactNode, useMemo } from "react";
import {
  Autocomplete,
  Dialog,
  Header,
  Input,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  SearchField,
  Text,
  useFilter,
} from "react-aria-components";
import { Kbd } from "@/components/ui";
import { cx } from "@/lib/cx";

export type PickerOption = {
  id: string;
  label: string;
  /** Muted text after the label (e.g. "Urgent" next to "P0", an email next to a name). */
  description?: string;
  /** Monospace text before the label, e.g. an item key. */
  prefix?: string;
  icon?: ReactNode;
  /** Extra words to match while filtering (slugs, keys, other-language names). */
  keywords?: string;
  /** A digit that picks the option while the filter is empty. Shown on the right. */
  shortcut?: string;
  section?: string;
};

export interface PropertyPickerProps {
  /** Accessible name of the dialog and placeholder of the filter, e.g. "Change status". */
  label: string;
  options: PickerOption[];
  /** single: Enter picks and closes. multiple: Enter toggles, Escape closes. */
  mode: "single" | "multiple";
  selected: ReadonlySet<string>;
  /** Single mode: the picked option. */
  onPick?: (id: string) => void;
  /** Multiple mode: the option was turned on or off. */
  onToggle?: (id: string, on: boolean) => void;
  /** Shortcut shown in the filter field (the key that opened the picker), e.g. "S". */
  shortcut?: string;
  /** Extra controls above the filter (e.g. the relation kind). */
  header?: ReactNode;
  className?: string;
}

const row =
  "group/opt relative flex min-h-(--control-h-md) cursor-default items-center gap-2 rounded-control px-2 text-sm text-fg outline-none " +
  "data-focused:bg-active data-pressed:bg-active data-disabled:opacity-45";

/**
 * Keyboard-first property picker: a filter field over a list, rendered inside a Popover (anchored
 * to a property button, a list row or a board card). The field keeps focus while arrow keys move
 * through the options (virtual focus). Digits pick an option directly while the field is empty.
 *
 *   <DialogTrigger><Button/><Popover><PropertyPicker .../></Popover></DialogTrigger>
 *
 * The caller closes the popover after `onPick`; the selection is rendered from `selected`.
 */
export function PropertyPicker({
  label,
  options,
  mode,
  selected,
  onPick,
  onToggle,
  shortcut,
  header,
  className,
}: PropertyPickerProps) {
  const { contains } = useFilter({ sensitivity: "base" });

  const sections = useMemo(() => {
    const out: Array<{ name: string | undefined; options: PickerOption[] }> = [];
    for (const o of options) {
      const last = out[out.length - 1];
      if (last && last.name === o.section) last.options.push(o);
      else out.push({ name: o.section, options: [o] });
    }
    return out;
  }, [options]);

  const byId = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);

  const choose = (id: string) => {
    if (mode === "single") onPick?.(id);
    else onToggle?.(id, !selected.has(id));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.currentTarget.value !== "" || e.metaKey || e.ctrlKey || e.altKey) return;
    if (!/^[0-9]$/.test(e.key)) return;
    const hit = options.find((o) => o.shortcut === e.key);
    if (hit) {
      e.preventDefault();
      choose(hit.id);
    }
  };

  const renderOption = (o: PickerOption) => (
    <ListBoxItem
      key={o.id}
      id={o.id}
      textValue={[o.prefix, o.label, o.description, o.keywords].filter(Boolean).join(" ")}
      className={row}
    >
      {() => {
        const on = selected.has(o.id);
        return (
          <>
            {mode === "multiple" ? (
              <span
                aria-hidden="true"
                className={cx(
                  "flex size-3.5 shrink-0 items-center justify-center rounded-mark border",
                  on ? "border-accent bg-accent text-accent-fg" : "border-line-strong",
                )}
              >
                {on ? <Check size={10} weight="bold" /> : null}
              </span>
            ) : null}
            {o.icon ? <span className="flex w-4 shrink-0 justify-center">{o.icon}</span> : null}
            {o.prefix ? <span className="shrink-0 font-mono text-xs text-fg-muted tnum">{o.prefix}</span> : null}
            <Text slot="label" className="min-w-0 truncate-title" title={o.label}>
              {o.label}
            </Text>
            {o.description ? (
              <Text slot="description" className="min-w-0 shrink truncate-title text-xs text-fg-subtle">
                {o.description}
              </Text>
            ) : null}
            <span className="ml-auto flex shrink-0 items-center gap-2 pl-2">
              {mode === "single" && on ? <Check size={14} aria-hidden="true" className="text-accent-text" /> : null}
              {o.shortcut ? <Kbd keys={[o.shortcut]} /> : null}
            </span>
          </>
        );
      }}
    </ListBoxItem>
  );

  return (
    <Dialog aria-label={label} className={cx("flex max-h-[min(420px,60dvh)] w-72 flex-col outline-none", className)}>
      {header}
      <Autocomplete filter={contains}>
        <SearchField
          aria-label={label}
          autoFocus
          className="flex shrink-0 items-center gap-2 border-b border-line px-3"
        >
          <MagnifyingGlass size={14} aria-hidden="true" className="shrink-0 text-fg-subtle" />
          <Input
            placeholder={`${label}…`}
            onKeyDown={onKeyDown}
            className="h-9 min-w-0 flex-1 bg-transparent text-sm text-fg outline-none placeholder:text-fg-subtle [&::-webkit-search-cancel-button]:hidden"
          />
          {shortcut ? <Kbd keys={[shortcut]} /> : null}
        </SearchField>
        <ListBox
          aria-label={label}
          selectionMode={mode === "multiple" ? "multiple" : "none"}
          selectedKeys={mode === "multiple" ? [...selected] : undefined}
          onSelectionChange={(keys) => {
            if (mode !== "multiple" || keys === "all") return;
            const next = new Set([...keys].map(String));
            for (const id of next) if (!selected.has(id)) onToggle?.(id, true);
            for (const id of selected) if (!next.has(id) && byId.has(id)) onToggle?.(id, false);
          }}
          onAction={mode === "single" ? (k) => onPick?.(String(k)) : undefined}
          className="scroll-thin min-h-0 flex-1 overflow-auto p-1 outline-none"
          renderEmptyState={() => <div className="px-2 py-3 text-sm text-fg-muted">{m.combo_no_results()}</div>}
        >
          {sections.map((s, i) =>
            s.name ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: section names can repeat; order is stable
              <ListBoxSection key={`${s.name}-${i}`} className="[&+&]:mt-1 [&+&]:border-t [&+&]:border-line [&+&]:pt-1">
                <Header className="px-2 pt-1.5 pb-1 text-2xs font-medium text-fg-subtle">{s.name}</Header>
                {s.options.map(renderOption)}
              </ListBoxSection>
            ) : (
              s.options.map(renderOption)
            ),
          )}
        </ListBox>
      </Autocomplete>
    </Dialog>
  );
}
